import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Info, History, CheckCircle2, Circle, Clock, AlertTriangle, Pencil, Plus, Lock, GripVertical } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function ReorderWrap({ items, onReorder, children }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
  );
  if (!onReorder) return <>{children}</>;
  const ids = items.map((i) => i.id);
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    onReorder(arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>{children}</SortableContext>
    </DndContext>
  );
}

function SortableRow({ id, className, children, ...rest }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 20 : undefined };
  return (
    <div ref={setNodeRef} style={style} className={`${className} relative pl-7 ${isDragging ? "bg-violet-50 shadow-lg rounded-lg" : ""}`} {...rest}>
      <button type="button" {...attributes} {...listeners} aria-label="Arrastar para mudar a ordem"
        className="absolute left-0 top-0 w-6 h-8 flex items-center justify-center text-slate-400 hover:text-violet-600 touch-none cursor-grab active:cursor-grabbing"
        data-testid={`${rest["data-testid"]}-drag`}>
        <GripVertical className="w-4 h-4" />
      </button>
      {children}
    </div>
  );
}

function SortableItemRow({ enabled, id, children, ...rest }) {
  if (!enabled) return <div {...rest}>{children}</div>;
  return <SortableRow id={id} {...rest}>{children}</SortableRow>;
}
import { api, BRL, formatDateBR, shownUnit } from "@/lib/api";
import ItemPhotoButton from "@/components/ItemPhotoButton";
import { BrandLogoImg } from "@/components/BrandLogoImg";

/**
 * Visual rendering of a budget that mirrors the official AMC PDF layout.
 * Used by both the admin BudgetDetail page and the school SchoolBudgetDetail page.
 */

const PRESTADOR = {
  name: "AMC Manutenção e Serviços LTDA-ME",
  cnpj: "02.406.909/0001-70",
  ie: "149.368.011.114",
  address: "Av. Elísio Teixeira Leite, 7737",
  cep: "02810-000 Parada de Taipas - SP",
  representante: "Amauri RG 13.428.929-8",
  whatsapp: "(11) 95664-9968",
  email: "amcescolas@gmail.com",
};

// Company data is editable in "Minha conta"; fetched once per page load.
let _companyCache = null;
const useCompany = () => {
  const [c, setC] = useState(_companyCache || PRESTADOR);
  useEffect(() => {
    if (_companyCache) return;
    api.get("/public/company", { silent: true }).then((r) => { _companyCache = { ...PRESTADOR, ...r.data }; setC(_companyCache); }).catch(() => {});
  }, []);
  return c;
};
export const resetCompanyCache = () => { _companyCache = null; };

const STATUS_LABELS = {
  aguardando: "Aguardando aprovação",
  aprovado: "Aprovado",
  reprovado: "Cancelado",
  iniciando: "Em andamento",
  finalizado: "Finalizado",
  nota_fiscal_gerada: "Nota fiscal gerada",
  nota_faturada: "Nota fiscal paga",
  deletado: "Deletado",
  ex_sistema: "Ex-sistema",
};

const STATUS_COLOR = {
  aguardando: "text-amber-600",
  aprovado: "text-green-600",
  reprovado: "text-red-600",
  iniciando: "text-blue-600",
  finalizado: "text-green-600",
  nota_fiscal_gerada: "text-red-600",
  nota_faturada: "text-violet-600",
  deletado: "text-slate-500",
  ex_sistema: "text-amber-700",
};

const WEEKDAY_PT = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
const MONTH_PT = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const PAYMENT_LABELS = {
  apos_nf: "Após a entrega da nota fiscal",
  entrada_30: "Precisa de entrada de 30%",
  entrada_50: "Precisa de entrada de 50%",
  manual: "",
  // legacy
  boleto: "Boleto bancário",
  pix: "PIX",
  cartao: "Cartão",
  transferencia: "Transferência",
  dinheiro: "Dinheiro",
  outro: "",
};

const SERVICE_TYPE_LABELS = {
  mao_de_obra: "Só mão de obra",
  material_mao_de_obra: "Inclui material e mão de obra",
  conforme_item: "De acordo com observação no item",
};

const fmtDateFull = (iso) => {
  if (!iso) return "—";
  const d = iso.includes("T") ? iso.split("T")[0] : iso;
  const [y, m, dd] = d.split("-").map((p) => parseInt(p, 10));
  if (!y || !m || !dd) return iso;
  const dt = new Date(y, m - 1, dd);
  // JS Monday=1, Sunday=0 ; weekday_pt starts at Monday
  const wdIdx = (dt.getDay() + 6) % 7;
  return `${WEEKDAY_PT[wdIdx]}, ${String(dd).padStart(2, "0")} ${MONTH_PT[m - 1]} - ${y}`;
};

const fmtDateShort = (iso) => {
  if (!iso) return "";
  const d = iso.includes("T") ? iso.split("T")[0] : iso;
  const [y, m, dd] = d.split("-").map((p) => parseInt(p, 10));
  if (!m || !dd) return iso;
  return `${String(dd).padStart(2, "0")}/${String(m).padStart(2, "0")}`;
};

const bbClean = (text) => {
  if (!text) return "";
  return String(text).replace(/\[(\/?)(b|i|u)\]/gi, (_m, c, t) => `<${c}${t.toLowerCase()}>`);
};

const Logo = ({ competitor, logoUrl }) => {
  if (competitor) {
    if (logoUrl) {
      return (
        <img
          src={logoUrl}
          alt={competitor}
          className="w-20 h-20 md:w-24 md:h-24 rounded-full object-contain bg-white shrink-0"
        />
      );
    }
    const initials = (competitor || "").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase() || "•";
    return (
      <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0" aria-label={competitor}>
        <span className="font-heading text-2xl md:text-3xl font-black tracking-tight">{initials}</span>
      </div>
    );
  }
  return (
    <BrandLogoImg
      alt="AMC Escolas"
      className="w-20 h-20 md:w-24 md:h-24 rounded-full object-contain shrink-0"
    />
  );
};


// ===== Competitor prose layout (mirrors BBH/WORK model PDFs) =====
function CompetitorSheet({ budget, school, comp }) {
  const stripBB = (s) => (s || "").replace(/\[\/?b\]/gi, "").replace(/\[\/?i\]/gi, "");
  const rep = comp.representative || "";
  const legalName = comp.legal_name || budget.competitor_name || "";
  const dateStr = (() => {
    const d = budget.budget_date ? new Date(`${budget.budget_date}T12:00:00`) : null;
    if (!d) return "";
    return `${WEEKDAY_PT[(d.getDay() + 6) % 7]}, ${String(d.getDate()).padStart(2, "0")} ${MONTH_PT[d.getMonth()]} - ${d.getFullYear()}`;
  })();
  const items = budget.items || [];
  const paymentBank = comp.payment_bank || "";
  const delivery = budget.delivery_days || 7;
  const validity = budget.validity_days || 30;
  const warranty = (budget.warranty || "30 dias").trim();

  return (
    <div className="bg-white rounded-xl border border-slate-100 card-shadow p-5 md:p-8 text-slate-800" data-testid="competitor-sheet">
      {/* Top-right: "Este orçamento foi elaborado por: Nome" */}
      <div className="text-right text-xs md:text-sm text-slate-700 mb-2">
        <p>Este orçamento foi elaborado por: <strong>{rep}</strong></p>
        <p>{dateStr}</p>
      </div>

      {/* Header: logo + legal name + CNPJ + fone */}
      <div className="flex items-center gap-4 mb-2">
        <Logo competitor={budget.competitor_name || legalName} logoUrl={comp.logo_url} />
        <div className="min-w-0 flex-1">
          <h1 className="font-heading text-lg md:text-2xl font-bold leading-tight">{legalName}</h1>
          {comp.address && <p className="text-xs md:text-sm text-slate-700">{comp.address}</p>}
          <p className="text-xs md:text-sm">
            <strong>CNPJ: {comp.cnpj}</strong>{comp.phone ? ` - fone: ${comp.phone}` : ""}
          </p>
        </div>
      </div>

      {/* Tagline */}
      {comp.tagline && (
        <p className="text-sm italic text-red-600 mb-5">{comp.tagline}</p>
      )}

      {/* Dados do Cliente */}
      <h3 className="font-bold text-sm mb-1">Dados do Cliente</h3>
      <div className="text-[12px] leading-6 mb-5">
        <p><strong>Escola:</strong> {school?.name || "—"}</p>
        {school?.cnpj && <p><strong>CNPJ:</strong> {school.cnpj}</p>}
        {school?.address && <p><strong>Endereço:</strong> {school.address}</p>}
        {school?.phone && <p><strong>Telefone:</strong> {school.phone}</p>}
        {school?.director_name && <p><strong>Diretor(a):</strong> {school.director_name}</p>}
      </div>

      {/* Discrição dos Serviços */}
      {(budget.reference || "").trim() && (
        <p className="text-[12px] mb-1.5"><b>Referência:</b> {budget.reference.trim()}</p>
      )}
      <h3 className="font-bold text-sm mb-2">Discrição dos serviços a serem realizados</h3>
      <div className="space-y-3 mb-5 text-[12px] leading-6">
        {items.map((it, idx) => (
          <div key={it.id || idx}>
            <p>
              <b>{idx + 1} -</b> {stripBB(it.description || "")}
            </p>
            {it.observation && <p className="text-slate-600">{stripBB(it.observation)}</p>}
            {!it.without_value && (it.quantity || it.unit_value) ? (
              <p className="text-[11px] text-slate-600">
                Qtd {it.quantity ?? 1} × {BRL(shownUnit(it))} = <b>{BRL(it.total || 0)}</b>
              </p>
            ) : null}
          </div>
        ))}
      </div>

      {/* Valor total em prosa */}
      <p className="text-[12px] mb-4">
        Os serviços acima descritos somados chegam ao valor total de: <strong>{BRL(budget.total)}</strong>
      </p>

      {/* Pagamento em prosa */}
      <p className="text-[12px] mb-4">
        Para o pagamento desde orçamento após o termino e entrega da nota fiscal, ficamos a disposição aceitamos,
        cheque, transferências bancárias no banco <strong>{paymentBank}</strong>.
      </p>

      {/* Prazos */}
      <p className="text-[12px] mb-1"><strong>Prazo para a entrega dos serviços</strong> {delivery} dias úteis</p>
      <p className="text-[12px] mb-4"><strong>Prazo de validade desta proposta</strong> {validity} dias corridos</p>

      {/* Garantia */}
      <p className="text-[11px] italic text-slate-600 mb-6">
        Os serviços acima *sem observações tem como padrão a garantia de <strong>{warranty}</strong> após a entrega da nota fiscal
        *Serviços com garantias diferenciadas serão descritos abaixo do item. Como observação extra.
      </p>

      {/* Stamp */}
      {comp.stamp_url && (
        <div className="mt-6">
          <img
            src={comp.stamp_url}
            alt={`Carimbo ${legalName}`}
            className="max-h-[180px] max-w-[380px] object-contain"
            data-testid="competitor-sheet-stamp"
          />
        </div>
      )}
    </div>
  );
}

export default function BudgetSheet({ budget, onTermClick, onItemHistoryClick, onItemCompleteClick, onItemDateClick, onItemPhotoUpdate, onItemEdit, onItemAdd, onReorder, showAdminNotes = false, canManageItems = false }) {
  const company = useCompany();
  if (!budget) return null;
  const school = budget.school || {};
  const status = budget.status || "aguardando";
  const statusLabel = STATUS_LABELS[status] || status;
  const statusColor = STATUS_COLOR[status] || "text-slate-600";
  // Items can only be completed when the budget is "iniciando" (or already "finalizado" via admin password)
  const itemsClickable = canManageItems && (status === "iniciando" || status === "finalizado" || !!budget.nfa_open);

  // If this budget is a competitor's, override prestador data (no AMC trace)
  const comp = budget.competitor_profile || null;
  const isCompetitor = !!comp;

  // ===== COMPETITOR PROSE LAYOUT (matches BBH/WORK model PDFs) =====
  if (isCompetitor) {
    return <CompetitorSheet budget={budget} school={school} comp={comp} />;
  }

  const brand = company;

  const items = budget.items || [];
  const totalBRL = BRL(budget.total || 0);
  const taxV = budget.tax_value || 0;
  const dpPct = Number(budget.down_payment_pct || 0);
  const dpValue = dpPct > 0 ? (budget.total || 0) * (dpPct / 100) : 0;
  const dpRest = dpPct > 0 ? (budget.total || 0) - dpValue : 0;

  const payKey = budget.payment_option || "";
  const paymentOther = (budget.payment_other || "").trim();
  const paymentFull =
    payKey === "manual" || payKey === "outro"
      ? paymentOther || "A combinar"
      : PAYMENT_LABELS[payKey] || paymentOther || "Após a entrega da nota fiscal";

  const serviceLabel = SERVICE_TYPE_LABELS[budget.service_type] || "Incluindo material e mão de obra";

  const warranty = (budget.warranty || "").trim() || "90 dias";
  const reference = (budget.reference || "").trim();
  const awareness = (budget.awareness_term || "").trim();

  const addrParts = [
    school.address,
    school.address_number,
    school.neighborhood,
    school.city,
    school.state,
  ].filter(Boolean);
  const cepPart = school.cep ? ` CEP ${school.cep}` : "";
  const fullAddr = addrParts.join(", ") + cepPart;

  const isClosed = ["finalizado", "nota_fiscal_gerada", "nota_faturada"].includes(status);
  const isApproved = ["aprovado", "iniciando"].includes(status);
  const isCancelled = status === "reprovado";

  return (
    <div
      data-testid="budget-sheet"
      className="bg-white rounded-2xl border border-slate-200 card-shadow mx-auto w-full max-w-[840px] print:border-0 print:shadow-none print:max-w-full print:rounded-none"
    >
      <div className="p-5 md:p-10 text-[13px] text-slate-800 leading-snug">
        {/* ========== TOP ROW ========== */}
        <div className="grid grid-cols-3 gap-3 text-[11px] md:text-[12px] mb-5">
          {isCompetitor ? <div /> : (
            <div className="font-semibold">
              <span className="text-slate-500">Status:</span>{" "}
              <span className={`font-bold ${statusColor}`}>{statusLabel}</span>
            </div>
          )}
          <div className="text-center font-bold">
            Orçamento número: <span className="text-[#1e3a8a]">{budget.number}</span>
            {!isCompetitor && (budget.nf_number || budget.invoice_number) ? (
              <div className="font-normal text-[11px] mt-0.5">
                Nota fiscal nº <span className="text-[#1e3a8a] font-bold">{budget.nf_number || budget.invoice_number}</span>
              </div>
            ) : null}
          </div>
          <div className="text-right text-slate-600 text-[11px] md:text-[12px]">
            {fmtDateFull(budget.budget_date)}
          </div>
        </div>

        {/* ========== BRAND / LOGO ========== */}
        <div className="flex items-center gap-4 mb-5">
          <Logo competitor={isCompetitor ? (budget.competitor_name || brand.name) : null} logoUrl={isCompetitor ? comp?.logo_url : null} />
          <div className="min-w-0">
            <h1 className="font-heading text-2xl md:text-4xl font-light text-slate-900 leading-tight">
              {isCompetitor ? brand.headline : "Manutenção e Serviços LTDA"}
            </h1>
            <p className="text-[10px] md:text-[11px] text-slate-600 mt-0.5">
              {brand.cnpj && (<><b>CNPJ:</b> {brand.cnpj}</>)}
              {brand.ie && (<>&nbsp;&nbsp; <b>I.E</b> {brand.ie}</>)}
            </p>
          </div>
        </div>

        {/* ========== PRESTADOR / CLIENTE ========== */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-[12px] mb-6">
          <div>
            <p className="font-bold text-[#1e3a8a] mb-1">Dados do Prestador</p>
            <p><b>Empresa:</b> {brand.name}</p>
            {brand.address && <p><b>Endereço:</b> {brand.address}</p>}
            {brand.cep && <p><b>CEP:</b> {brand.cep}</p>}
            {brand.representante && <p><b>Representante:</b> {brand.representante}</p>}
            {brand.whatsapp && <p><b>{isCompetitor ? "Telefone" : "WhatsApp"}:</b> {brand.whatsapp}</p>}
            {brand.email && <p><b>E-mail:</b> {brand.email}</p>}
          </div>
          <div>
            <p className="font-bold text-[#1e3a8a] mb-1">Dados do Cliente</p>
            <p><b>Escola:</b> {school.name || "—"}</p>
            {school.cnpj && (
              <p className="flex items-center gap-1 flex-wrap">
                <b>CNPJ:</b>
                <span>{school.cnpj}</span>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(school.cnpj);
                      toast.success("CNPJ copiado");
                    } catch { toast.error("Erro ao copiar"); }
                  }}
                  data-testid="btn-copy-cnpj-budget"
                  title="Copiar CNPJ"
                  className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-800 text-white hover:bg-slate-700 print:hidden"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                </button>
              </p>
            )}
            {school.phone && <p><b>Telefone:</b> {school.phone}</p>}
            {fullAddr && <p><b>Endereço:</b> {fullAddr}</p>}
            {(school.director_name || school.director_email) && (
              <p>
                {school.director_name && <><b>Diretor(a):</b> {school.director_name}</>}
                {school.director_email && (
                  <> {school.director_name ? " / " : ""}
                    <b>E-mail:</b> {school.director_email}
                  </>
                )}
              </p>
            )}
          </div>
        </div>

        {/* ========== DISCRIÇÃO DO(S) SERVIÇO(S) ========== */}
        {reference && (
          <p className="text-[12px] text-slate-700 mb-1.5">
            <b>Referência:</b> {reference}
          </p>
        )}
        <h3 className="font-bold text-[13px] text-slate-900 mb-2">Discrição do(s) Serviço(s)</h3>
        <div className="space-y-2 mb-3">
          {items.length === 0 ? (
            <p className="text-center text-slate-500 py-4 text-[12px]">Nenhum item cadastrado.</p>
          ) : (
            <ReorderWrap items={items} onReorder={onReorder}>{items.map((it, idx) => {
              const done = !!it.completed;
              const doneAt = fmtDateShort(it.completed_at);
              return (
                <SortableItemRow
                  key={it.id}
                  id={it.id}
                  enabled={!!onReorder}
                  data-testid={`sheet-item-${idx}`}
                  className="border-b border-slate-100 pb-2 last:border-b-0"
                >
                  {/* Linha 1: badge + descrição (largura total) */}
                  <div className="flex items-start gap-2 md:gap-3">
                    {isCompetitor ? null : itemsClickable ? (
                      <button
                        type="button"
                        onClick={() => onItemCompleteClick?.(it)}
                        data-testid={`sheet-item-toggle-${idx}`}
                        className={`inline-flex items-center shrink-0 gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors ${
                          done
                            ? "bg-green-100 text-green-700 hover:bg-green-200"
                            : "bg-amber-50 text-amber-700 hover:bg-amber-100"
                        }`}
                        title={done ? "Clique para reabrir" : "Clique para concluir"}
                      >
                        {done ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                        {done ? "Concluído" : "Pendente"}
                      </button>
                    ) : (
                      <span
                        className={`inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                          done ? "bg-green-100 text-green-700" : "bg-amber-50 text-amber-700"
                        }`}
                        title={canManageItems ? "Mude o status para \"Em andamento\" para habilitar" : ""}
                      >
                        {done ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                        {done ? "Concluído" : "Pendente"}
                      </span>
                    )}
                    <div
                      className={`flex-1 min-w-0 ${onItemEdit ? "cursor-pointer rounded hover:bg-violet-50/60 -mx-1 px-1 transition-colors" : ""}`}
                      onClick={onItemEdit ? () => onItemEdit(it) : undefined}
                      data-testid={`sheet-item-text-${idx}`}
                    >
                      <p className="text-[12px] md:text-[13px] leading-snug break-words">
                        <b>{idx + 1} -</b>{" "}
                        <span
                          dangerouslySetInnerHTML={{
                            __html: bbClean(it.description || ""),
                          }}
                        />
                      </p>
                      {it.observation && (
                        <p
                          className="text-[11px] text-slate-500 mt-0.5 break-words"
                          dangerouslySetInnerHTML={{ __html: bbClean(it.observation) }}
                        />
                      )}
                    </div>
                  </div>

                  {/* Linha 2: Qtd × Unit = Total (sob a descrição, largura total) */}
                  {!it.without_value && (it.quantity || it.unit_value) ? (
                    <p className="text-[11px] text-slate-600 mt-1 pl-0 md:pl-[72px]">
                      Qtd {it.quantity ?? 1} × {BRL(shownUnit(it))} ={" "}
                      <b>{BRL(it.total || 0)}</b>
                    </p>
                  ) : null}

                  {/* Linha 3: meta (Finalizado + Histórico + Fotos) — não briga com descrição */}
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1 pl-0 md:pl-[72px]">
                    {showAdminNotes && it.admin_note && (
                      <p className="w-full text-[10.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 whitespace-pre-wrap break-words" data-testid={`sheet-item-admin-note-${idx}`}>
                        <Lock className="w-3 h-3 inline mr-1 -mt-0.5" />{it.admin_note}
                      </p>
                    )}
                    {onItemEdit && (
                      <button
                        type="button"
                        onClick={() => onItemEdit(it)}
                        data-testid={`sheet-item-edit-${idx}`}
                        className="inline-flex items-center gap-1 text-[10px] text-violet-700 hover:underline font-semibold"
                      >
                        <Pencil className="w-3 h-3" /> Editar
                      </button>
                    )}
                    {done && doneAt && (
                      itemsClickable ? (
                        <button
                          type="button"
                          onClick={() => onItemDateClick?.(it)}
                          data-testid={`sheet-item-date-${idx}`}
                          className="text-[10px] text-slate-500 whitespace-nowrap hover:text-[#7c3aed] hover:underline"
                          title="Clique para alterar a data"
                        >
                          Finalizado em: <b>{doneAt}</b>
                        </button>
                      ) : (
                        <span className="text-[10px] text-slate-500 whitespace-nowrap">
                          Finalizado em: {doneAt}
                        </span>
                      )
                    )}
                    {(it.versions?.length > 0 || Number(it.discount) > 0) && onItemHistoryClick && (
                      <button
                        type="button"
                        onClick={() => onItemHistoryClick(it.id)}
                        data-testid={`sheet-item-history-${idx}`}
                        className="inline-flex items-center gap-1 text-[10px] text-[#7c3aed] hover:underline font-semibold"
                        title={`${it.versions?.length || 0} alteração(ões)`}
                      >
                        <History className="w-3 h-3" />
                        {it.versions?.length ? `Histórico (${it.versions.length})` : "Histórico"}
                      </button>
                    )}
                    {/* Botão rápido de fotos (antes/depois) */}
                    {!isCompetitor && itemsClickable && (
                      <ItemPhotoButton
                        budgetId={budget.id}
                        item={it}
                        onUpdate={(updated) => onItemPhotoUpdate?.(it.id, updated)}
                        testid={`sheet-item-photo-${idx}`}
                      />
                    )}
                  </div>
                </SortableItemRow>
              );
            })}</ReorderWrap>
          )}
          {onItemAdd && (
            <button
              type="button"
              onClick={onItemAdd}
              data-testid="sheet-item-add"
              className="w-full mt-1 h-9 rounded-lg border-2 border-dashed border-violet-300 text-violet-700 text-[12px] font-bold inline-flex items-center justify-center gap-1.5 hover:bg-violet-50 transition-colors"
            >
              <Plus className="w-4 h-4" /> Adicionar item
            </button>
          )}
        </div>

        {!isCompetitor && (
          <div className="flex items-center flex-wrap gap-3 mb-6">
            <button
              type="button"
              data-testid="sheet-terms-link"
              onClick={onTermClick}
              className="inline-flex items-center gap-1.5 text-[12px] text-blue-600 hover:underline font-bold"
            >
              <Info className="w-3.5 h-3.5" />
              Termo de ciência das condições do orçamento
            </button>
          {budget.terms_accepted && (
            <span
              data-testid="sheet-terms-accepted-badge"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5"
            >
              ✓ Aceito
              {budget.terms_accepted_by && <> por {budget.terms_accepted_by}</>}
              {budget.terms_accepted_at && (
                <> em {new Date(budget.terms_accepted_at).toLocaleDateString("pt-BR")}</>
              )}
            </span>
          )}
          </div>
        )}

        {/* ========== OBSERVAÇÃO GERAL (destaque) ========== */}
        {budget.observation && budget.observation.trim() && (
          <div
            data-testid="sheet-observation"
            className="flex items-start gap-3 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-3 mb-5"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-black uppercase tracking-wider text-amber-700 mb-1">
                Atenção — Observação
              </p>
              <p className="text-[13px] font-bold text-amber-900 whitespace-pre-wrap leading-snug">
                {budget.observation}
              </p>
            </div>
          </div>
        )}

        {/* ========== HONORÁRIOS ========== */}
        <h3 className="font-bold text-[13px] text-slate-900 mb-1 mt-4">HONORÁRIOS</h3>
        <p className="text-[12px] leading-snug mb-0.5">
          Para a execução dos serviços acima relacionados, cobramos a importância total de:{" "}
          <b className="text-[14px]">{totalBRL}</b>
        </p>
        <p className="text-[12px] leading-snug mb-0.5">{serviceLabel}</p>
        {dpPct > 0 && budget.total > 0 && (
          <p className="text-[12px] leading-snug mb-0.5">
            <b>Entrada de {dpPct}%:</b> {BRL(dpValue)} · <b>Saldo após nota fiscal:</b> {BRL(dpRest)}
          </p>
        )}
        {taxV > 0 && (
          <p className="text-[11px] text-red-600 leading-snug mb-0.5">
            <b>Valor aproximado dos impostos para esse orçamento:</b> {BRL(taxV)}{" "}
            <b>Observação:</b> Esse valor já encontra-se embutido no valor final total acima.
          </p>
        )}

        {/* ========== FORMA DE PAGAMENTO ========== */}
        <h3 className="font-bold text-[13px] text-slate-900 mb-1 mt-4">FORMA DE PAGAMENTO</h3>
        <p className="text-[12px] leading-snug mb-0.5">
          A forma para o pagamento deste orçamento será <b>{paymentFull}</b>, por transferência,
          boleto, cheque, máquina de cartão ou depósito bancário
          {isCompetitor
            ? (comp?.payment_bank ? <>, <b>{comp.payment_bank}</b>.</> : <>.</>)
            : <>, <b>Banco do Brasil – agência 635-1 – Conta Corrente 100663-0</b>.</>}
        </p>

        {/* ========== PRAZO DE ENTREGA ========== */}
        <h3 className="font-bold text-[13px] text-slate-900 mb-1 mt-4">PRAZO DE ENTREGA DOS SERVIÇOS</h3>
        <p className="text-[12px] leading-snug mb-0.5">
          Os serviços relacionados serão entregues no prazo de{" "}
          <b>{budget.delivery_days ?? 7} Dias Úteis</b> (podendo ser alterado, devido a condições do
          tempo, entrega de material, etc).
        </p>

        {/* ========== GARANTIA ========== */}
        <h3 className="font-bold text-[13px] text-slate-900 mb-1 mt-4">GARANTIA DOS SERVIÇOS</h3>
        <p className="text-[12px] leading-snug mb-0.5">
          OS SERVIÇOS ACIMA RELACIONADOS TEM O PRAZO DE GARANTIA DE:{" "}
          <b className="text-red-600">*{warranty}</b>
        </p>

        {/* ========== VALIDADE ========== */}
        <h3 className="font-bold text-[13px] text-slate-900 mb-1 mt-4">
          {isApproved ? "SITUAÇÃO DESTE ORÇAMENTO" : "VALIDADE DESTE ORÇAMENTO"}
        </h3>
        {isClosed ? (
          <p className="text-[12px] mb-8">
            Está proposta de serviços foi{" "}
            <b className="text-green-600">FINALIZADA</b> em{" "}
            <b>{fmtDateFull(budget.finalized_at || budget.updated_at)}</b>. Atenciosamente,
          </p>
        ) : isApproved ? (
          <p className="text-[12px] mb-8">
            Esta proposta foi <b className="text-green-600">APROVADA</b> pela escola
            {status === "iniciando" ? " e já está em andamento" : ""}. Atenciosamente,
          </p>
        ) : isCancelled ? (
          <p className="text-[12px] mb-8">
            Esta proposta foi <b className="text-red-600">CANCELADA</b>.
          </p>
        ) : (
          <p className="text-[12px] mb-8">
            Esta proposta de serviços é válida até{" "}
            <b>{formatDateBR(budget.validity_date)}</b>. Atenciosamente,
          </p>
        )}

        {/* ========== SIGNATURES ========== */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 pt-4 items-center">
          <div className="flex justify-center">
            {isCompetitor ? (
              comp?.stamp_url ? (
                <img
                  src={comp.stamp_url}
                  alt={`Carimbo ${brand.name}`}
                  className="max-h-[180px] max-w-[320px] object-contain"
                  data-testid="competitor-stamp"
                />
              ) : (
                <div className="text-center text-[11px] text-slate-600 pt-6 w-full max-w-[280px]">
                  <div className="border-t border-slate-400 mx-auto w-full" />
                  <p className="font-bold mt-2 text-[12px]">
                    {brand.representante || brand.name}
                  </p>
                  {brand.cnpj && <p className="text-[10px] text-slate-500">CNPJ: {brand.cnpj}</p>}
                </div>
              )
            ) : (
              <img
                src="/amc-assinatura.jpg"
                alt="Carimbo e assinatura AMC Manutenção e Serviços"
                className="max-w-[280px] w-full object-contain"
                data-testid="budget-stamp"
              />
            )}
          </div>
          <div className="text-center pt-6">
            <div className="border-t border-slate-400 mx-auto w-full max-w-xs" />
            <p className="font-bold mt-2 text-[12px]">Diretor(a).</p>
          </div>
        </div>
      </div>
    </div>
  );
}
